/**
 * A few sentences with words missing (`gap_text`,
 * [D-184](../../../docs/DECISIONS.md#d-184)).
 *
 * The owner asked for it: *"It gives you a paragraph worth of sentences, and you
 * have to then type in the missing words. At the beginning you might give a few
 * letters it starts with and make it progressively harder."* And a companion:
 * *"instead of typing the words, you get options for what you can insert"*,
 * more often in the early books, typing more in the later ones.
 *
 * Both are one exercise type with a `mode`, written the same way: a gap is a
 * word in square brackets, with any other spelling that is also right after a
 * bar — `I [live] in Denmark.`, `It is [grey|gray].` Everything here is pure,
 * so the parsing, the hints and the marking are each proved once and shared by
 * the linter, the authoring tool's twin in Python, and the screen.
 */
import { normalise, scoreTyped } from './scoring.js';
const GAP = /\[([^\]]+)\]/g;
/**
 * Split one line into words and gaps, numbering the gaps from `from`.
 *
 * A bracket with nothing usable in it is left as text rather than made into a
 * gap nobody can fill; the linter reports it.
 */
export function parseGaps(line, from = 0) {
    const pieces = [];
    let at = 0;
    let index = from;
    for (const match of line.matchAll(GAP)) {
        // The group always matched: the pattern cannot match without it.
        const spellings = String(match[1])
            .split('|')
            .map((one) => one.trim())
            .filter((one) => one.length > 0);
        const answer = spellings[0];
        if (answer === undefined)
            continue;
        const start = match.index;
        if (start > at)
            pieces.push({ kind: 'text', text: line.slice(at, start) });
        pieces.push({ kind: 'gap', index, answer, accepted: spellings });
        index += 1;
        at = start + match[0].length;
    }
    if (at < line.length)
        pieces.push({ kind: 'text', text: line.slice(at) });
    return pieces;
}
/** Every gap in a paragraph, in reading order. */
export function gapsIn(lines) {
    const found = [];
    for (const line of lines) {
        for (const piece of parseGaps(line, found.length)) {
            if (piece.kind === 'gap')
                found.push(piece);
        }
    }
    return found;
}
/**
 * How many letters a typed gap starts with, by book.
 *
 * Two in the first two books, one in the middle two, none in the last. The help
 * goes away as the books go on, the same way the bands take scaffolding away
 * ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md)).
 */
export const HINT_LETTERS_BY_BOOK = {
    1: 2,
    2: 2,
    3: 1,
    4: 1,
    5: 0,
};
/** The most letters any gap may give away. */
export const MAX_HINT_LETTERS = 3;
/**
 * The letters a typed gap shows before the box.
 *
 * Never more than half the word: *"ca"* of *cat* would leave one letter to
 * type, which is not a question.
 */
export function hintFor(answer, hintLetters) {
    const letters = Math.max(0, Math.min(hintLetters, Math.floor(answer.length / 2)));
    return answer.slice(0, letters);
}
/**
 * Mark a paragraph.
 *
 * A chosen word must be one of the right spellings. A typed one is forgiven a
 * slip of the finger by the same rules as every other typed answer
 * ([`scoring.ts`](./scoring.ts)) — *wached* for *watched* is right, with the
 * spelling shown — because this is a reading-and-remembering exercise, not a
 * spelling test. `given` is the whole word: for a typed gap, the letters shown
 * and the letters typed together.
 */
export function markGaps(gaps, given, mode) {
    const marks = gaps.map((gap, at) => {
        const put = given[at] ?? '';
        if (mode === 'choose') {
            const right = gap.accepted.some((one) => normalise(one) === normalise(put));
            return { right, given: put, correction: right ? null : gap.answer };
        }
        const scored = scoreTyped(put, { accepted: gap.accepted });
        if (scored.outcome !== 'correct') {
            return { right: false, given: put, correction: gap.answer };
        }
        return {
            right: true,
            given: put,
            correction: scored.exact
                ? null
                : // `matched` is one of these, folded, so the find cannot miss.
                    gap.accepted.find((one) => normalise(one) === scored.matched),
        };
    });
    const right = marks.filter((mark) => mark.right).length;
    const score = gaps.length === 0 ? 0 : right / gaps.length;
    return {
        marks,
        score,
        outcome: score === 1 ? 'correct' : score >= 0.5 ? 'partial' : 'incorrect',
    };
}
//# sourceMappingURL=gaps.js.map